// src/pages/VerifyEmail.js
//
// Step 2 of 4. Polls for verification so the person doesn't have to come back
// and refresh — they click the link in another tab and this page moves on.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { sendVerificationEmail } from "../utils/sendVerificationEmail";
import { isEmailConfirmed } from "../utils/studentLogin";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();

  // Register.js hands this over when the first send failed. Without it this
  // page confidently claims an email was sent that never left the building,
  // and the person waits for something that isn't coming.
  const firstSendFailed = location.state?.verificationSent === false;

  const [status, setStatus] = useState("");
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  const check = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    await user.reload();
    if (isEmailConfirmed(user)) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, 4000);
    return () => clearInterval(timerRef.current);
  }, [check]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const resend = async () => {
    const user = auth.currentUser;
    if (!user || cooldown > 0) return;
    setResending(true);
    try {
      await sendVerificationEmail(user);
      setStatus("Sent. Check your inbox — and your spam folder.");
      setCooldown(30);
    } catch (err) {
      console.error("Resend error:", err);
      setStatus("Couldn't resend just yet. Wait a moment and try again.");
    }
    setResending(false);
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h1 style={s.title}>Confirm your email</h1>

        {firstSendFailed ? (
          <p style={s.body}>
            Your account is created — but we couldn't get the confirmation
            email out to{" "}
            <strong>{auth.currentUser?.email || "your email address"}</strong>.
            Tap below to send it again.
          </p>
        ) : (
          <p style={s.body}>
            We sent a confirmation link to{" "}
            <strong>{auth.currentUser?.email || "your email address"}</strong>.
            Open it and this page will move you along automatically.
          </p>
        )}

        {/* Shown in both cases, and directly under the address rather than
            buried in the resend confirmation. Gmail and Outlook both file
            Firebase's no-reply verification mail as promotions or junk often
            enough that "it never arrived" is usually "it arrived somewhere
            else" — and someone who doesn't find it assumes signup failed and
            tries again, which lands them on "email already in use". */}
        <p style={s.spam}>Please check your spam folder.</p>

        {firstSendFailed && !status && (
          <p style={s.warn}>
            Nothing has gone wrong with your account — you're signed in and
            everything you entered was saved.
          </p>
        )}

        {status && <p style={s.status}>{status}</p>}

        <button
          type="button"
          style={{ ...s.primary, opacity: resending || cooldown > 0 ? 0.6 : 1 }}
          onClick={resend}
          disabled={resending || cooldown > 0}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend confirmation email"}
        </button>

        <button type="button" style={s.secondary} onClick={check}>
          I've confirmed — continue
        </button>

        <button
          type="button"
          style={s.link}
          onClick={async () => {
            await signOut(auth);
            navigate("/login", { replace: true });
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    background: "#fff",
    maxWidth: 460,
    width: "100%",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
    textAlign: "center",
  },
  title: { margin: 0, fontSize: "clamp(1.5rem, 4vw, 1.9rem)", color: "#111" },
  body: { color: "#444", fontSize: 15, lineHeight: 1.6, marginTop: 12 },
  spam: {
    color: "#b91c1c",
    fontWeight: 800,
    fontSize: 15,
    marginTop: 12,
    marginBottom: 0,
    letterSpacing: "0.01em",
  },
  status: { color: "#065f46", fontSize: 14, marginTop: 10 },
  warn: {
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    color: "#78350f",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 13.5,
    lineHeight: 1.5,
    marginTop: 12,
    textAlign: "left",
  },
  primary: {
    width: "100%",
    marginTop: 20,
    padding: "12px 18px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  secondary: {
    width: "100%",
    marginTop: 10,
    padding: "12px 18px",
    borderRadius: 8,
    border: "none",
    background: "#e5e7eb",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  link: {
    marginTop: 14,
    background: "none",
    border: "none",
    color: "#6b7280",
    fontSize: 13,
    cursor: "pointer",
    textDecoration: "underline",
  },
};
